import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../lib/api';

// Helper to clear auth and redirect
const handleAuthError = () => {
  localStorage.removeItem('pai-auth');
  toast.error('Sessão expirada. Faça login novamente.');
  // Redirect to login (use setTimeout to allow toast to show)
  setTimeout(() => {
    window.location.href = '/login';
  }, 500);
};

// Configure axios interceptor to add token and tenant
axios.interceptors.request.use(
  (config) => {
    // Add tenant slug header if selected
    const selectedTenant = localStorage.getItem('selected_tenant');
    if (selectedTenant) {
      config.headers['X-Tenant-Slug'] = selectedTenant;
    }
    
    // Add auth token
    const authData = localStorage.getItem('pai-auth');
    if (authData) {
      try {
        const { state } = JSON.parse(authData);
        if (state?.token) {
          config.headers.Authorization = `Bearer ${state.token}`;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Configure axios interceptor for 401 responses
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if already on login page or during login attempt
      const isLoginRequest = error.config?.url?.includes('/auth/otp');
      if (!isLoginRequest && !window.location.pathname.includes('/login')) {
        handleAuthError();
      }
    }
    return Promise.reject(error);
  }
);

// Auth Store
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      tenant: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // OTP Passwordless Auth - Send OTP
      sendOtp: async (email) => {
        set({ isLoading: true, error: null });
        try {
          const response = await axios.post(`${API_URL}/api/auth/otp/send`, { email });
          set({ isLoading: false });
          return { 
            success: true, 
            message: response.data.message,
            dev_otp: response.data.dev_otp // DEV mode only
          };
        } catch (error) {
          const message = error.response?.data?.detail || 'Erro ao enviar código';
          set({ error: message, isLoading: false });
          return { error: message };
        }
      },

      // OTP Passwordless Auth - Verify OTP
      verifyOtp: async (email, otp_code) => {
        set({ isLoading: true, error: null });
        try {
          const response = await axios.post(`${API_URL}/api/auth/otp/verify`, {
            email,
            otp_code
          });

          const { access_token, user, tenant } = response.data;

          set({
            token: access_token,
            user,
            tenant,
            isAuthenticated: true,
            isLoading: false
          });

          // Set default auth header
          axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || 'Código inválido ou expirado';
          set({ error: message, isLoading: false });
          return { error: message };
        }
      },

      // Legacy password login (backward compatibility)
      login: async (email, password, totpCode = null) => {
        set({ isLoading: true, error: null });
        try {
          // Get selected tenant slug from localStorage
          const selectedTenant = localStorage.getItem('selected_tenant');
          
          const response = await axios.post(`${API_URL}/api/auth/login`, {
            email,
            password,
            totp_code: totpCode,
            tenant_slug: selectedTenant || null
          });

          const { access_token, user, tenant, requires_2fa } = response.data;

          if (requires_2fa) {
            set({ isLoading: false });
            return { requires_2fa: true, user };
          }

          set({
            token: access_token,
            user,
            tenant,
            isAuthenticated: true,
            isLoading: false
          });

          axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || 'Erro ao fazer login';
          set({ error: message, isLoading: false });
          return { error: message };
        }
      },

      logout: () => {
        delete axios.defaults.headers.common['Authorization'];
        set({
          user: null,
          tenant: null,
          token: null,
          isAuthenticated: false,
          error: null
        });
      },

      // Atualiza o token (usado após troca de senha ou logout-all-devices,
      // onde o backend emite um novo JWT compatível com o novo token_version).
      // CRÍTICO: gravamos direto no localStorage 'pai-auth' (síncrono) ANTES de
      // chamar set() do zustand, porque o interceptor de axios lê do localStorage,
      // e o persist middleware do zustand pode demorar alguns ms pra gravar — o que
      // faria a próxima requisição enviar o token antigo e cair em 401 (auto-logout).
      setToken: (newToken) => {
        if (!newToken) return;
        try {
          const raw = localStorage.getItem('pai-auth');
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.state) {
              parsed.state.token = newToken;
              localStorage.setItem('pai-auth', JSON.stringify(parsed));
            }
          }
        } catch (e) {
          // se der erro de parse, segue mesmo assim — o set() abaixo vai disparar persist
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        set({ token: newToken });
      },

      fetchMe: async () => {
        const token = get().token;
        if (!token) return;

        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await axios.get(`${API_URL}/api/auth/me`);
          set({
            user: response.data.user,
            tenant: response.data.tenant,
            isAuthenticated: true
          });
        } catch (error) {
          get().logout();
        }
      },

      clearError: () => set({ error: null })
    }),
    {
      name: 'pai-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        tenant: state.tenant,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
);

// Tenant/Theme Store
export const useTenantStore = create((set) => ({
  branding: null,
  isLoading: false,
  selectedTenantSlug: localStorage.getItem('selected_tenant') || null,

  setSelectedTenant: (slug) => {
    localStorage.setItem('selected_tenant', slug);
    set({ selectedTenantSlug: slug });
  },

  clearSelectedTenant: () => {
    localStorage.removeItem('selected_tenant');
    set({ selectedTenantSlug: null });
  },

  fetchBranding: async () => {
    set({ isLoading: true });
    try {
      const selectedSlug = localStorage.getItem('selected_tenant');
      let url = `${API_URL}/api/tenants/branding`;
      
      // If a specific tenant is selected (dev mode), fetch its branding
      if (selectedSlug) {
        try {
          const tenantRes = await axios.get(`${API_URL}/api/tenants/by-slug/${selectedSlug}`);
          const tenantData = tenantRes.data;
          set({ 
            branding: {
              tenant_name: tenantData.name,
              brand_name: tenantData.brand_name,
              logo_url: tenantData.logo_url,
              primary_color: tenantData.primary_color,
              accent_color: tenantData.accent_color,
            }, 
            isLoading: false 
          });
          
          // Apply branding to CSS variables
          if (tenantData.primary_color) {
            document.documentElement.style.setProperty('--tenant-primary-hex', tenantData.primary_color);
          }
          if (tenantData.accent_color) {
            document.documentElement.style.setProperty('--tenant-accent-hex', tenantData.accent_color);
          }
          return;
        } catch (e) {
          // Fall back to default branding
        }
      }
      
      const response = await axios.get(url);
      set({ branding: response.data, isLoading: false });
      
      // Apply branding to CSS variables
      const { primary_color, accent_color, neutral_bg, neutral_text } = response.data;
      if (primary_color) {
        document.documentElement.style.setProperty('--tenant-primary-hex', primary_color);
      }
      if (accent_color) {
        document.documentElement.style.setProperty('--tenant-accent-hex', accent_color);
      }
    } catch (error) {
      set({ isLoading: false });
    }
  },

  setBranding: (branding) => set({ branding })
}));

// Finance Store
export const useFinanceStore = create((set, get) => ({
  // Bank Accounts
  bankAccounts: [],
  bankAccountsLoading: false,
  
  // Payables
  payables: [],
  payablesLoading: false,
  payablesSummary: {},
  payablesTotal: 0,
  payablesPage: 1,
  
  // Receivables
  receivables: [],
  receivablesLoading: false,
  receivablesTotal: 0,
  receivablesPage: 1,
  
  // Installment Plans
  installmentPlans: [],
  installmentPlansLoading: false,
  
  // Bank Transactions
  bankTransactions: [],
  bankTransactionsLoading: false,
  bankTransactionsTotal: 0,
  bankTransactionsPendingCount: 0,
  
  // Cash Flow
  cashFlow: null,
  cashFlowLoading: false,
  
  // Dashboard
  dashboard: null,
  dashboardLoading: false,
  dashboardError: null,
  
  // Vendors & Customers
  vendors: [],
  customers: [],
  categories: [],
  costCenters: [],

  // Actions
  fetchBankAccounts: async ({ includeInactive = false } = {}) => {
    set({ bankAccountsLoading: true });
    try {
      const url = `${API_URL}/api/finance/bank-accounts${includeInactive ? '?include_inactive=true' : ''}`;
      const response = await axios.get(url);
      set({ bankAccounts: response.data, bankAccountsLoading: false });
    } catch (error) {
      set({ bankAccountsLoading: false });
      throw error;
    }
  },

  reactivateBankAccount: async (id) => {
    const response = await axios.post(`${API_URL}/api/finance/bank-accounts/${id}/reactivate`);
    set((state) => ({
      bankAccounts: state.bankAccounts.map(acc => acc.id === id ? { ...acc, is_active: true } : acc)
    }));
    return response.data;
  },

  createBankAccount: async (data) => {
    const response = await axios.post(`${API_URL}/api/finance/bank-accounts`, data);
    set((state) => ({
      bankAccounts: [...state.bankAccounts, response.data]
    }));
    return response.data;
  },

  updateBankAccount: async (id, data) => {
    const response = await axios.put(`${API_URL}/api/finance/bank-accounts/${id}`, data);
    set((state) => ({
      bankAccounts: state.bankAccounts.map(acc => acc.id === id ? response.data : acc)
    }));
    return response.data;
  },

  deleteBankAccount: async (id) => {
    const response = await axios.delete(`${API_URL}/api/finance/bank-accounts/${id}`);
    set((state) => ({
      bankAccounts: state.bankAccounts.filter(acc => acc.id !== id)
    }));
    return response.data;
  },

  importBankTransactions: async (accountId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(
      `${API_URL}/api/finance/bank-accounts/${accountId}/import`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  fetchBankTransactions: async (params = {}) => {
    set({ bankTransactionsLoading: true });
    try {
      const response = await axios.get(`${API_URL}/api/finance/bank-transactions`, { params });
      set({
        bankTransactions: response.data.transactions,
        bankTransactionsTotal: response.data.total,
        bankTransactionsPendingCount: response.data.pending_reconciliation_count,
        bankTransactionsLoading: false
      });
    } catch (error) {
      set({ bankTransactionsLoading: false });
      throw error;
    }
  },

  fetchPayables: async (params = {}) => {
    set({ payablesLoading: true });
    try {
      const response = await axios.get(`${API_URL}/api/finance/payables`, { params });
      set({
        payables: response.data.payables,
        payablesSummary: response.data.summary,
        payablesTotal: response.data.total,
        payablesPage: response.data.page,
        payablesLoading: false
      });
    } catch (error) {
      set({ payablesLoading: false });
      throw error;
    }
  },

  createPayable: async (data) => {
    const response = await axios.post(`${API_URL}/api/finance/payables`, data);
    await get().fetchPayables();
    return response.data;
  },

  updatePayable: async (id, data) => {
    const response = await axios.put(`${API_URL}/api/finance/payables/${id}`, data);
    await get().fetchPayables();
    return response.data;
  },

  payPayable: async (id, data) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });
    const response = await axios.post(`${API_URL}/api/finance/payables/${id}/pay`, formData);
    await get().fetchPayables();
    return response.data;
  },

  fetchInstallmentPlans: async (params = {}) => {
    set({ installmentPlansLoading: true });
    try {
      const response = await axios.get(`${API_URL}/api/finance/installment-plans`, { params });
      set({
        installmentPlans: response.data.plans,
        installmentPlansLoading: false
      });
    } catch (error) {
      set({ installmentPlansLoading: false });
      throw error;
    }
  },

  createInstallmentPlan: async (data) => {
    const response = await axios.post(`${API_URL}/api/finance/installment-plans`, data);
    await get().fetchInstallmentPlans();
    await get().fetchPayables();
    return response.data;
  },

  fetchReceivables: async (params = {}, { silent = false } = {}) => {
    if (!silent) set({ receivablesLoading: true });
    try {
      const response = await axios.get(`${API_URL}/api/finance/receivables`, { params });
      set({
        receivables: response.data.receivables,
        receivablesTotal: response.data.total,
        receivablesPage: response.data.page,
        receivablesLoading: false
      });
    } catch (error) {
      set({ receivablesLoading: false });
      if (!silent) throw error;
    }
  },

  // Remoção otimista local (sem chamada de API) — usado depois de DELETE bem-sucedido
  // pra não mostrar "tela vazia" enquanto o fetch chega.
  removeReceivableLocal: (id) => {
    set(state => ({ receivables: (state.receivables || []).filter(r => r.id !== id) }));
  },

  createReceivable: async (data) => {
    const response = await axios.post(`${API_URL}/api/finance/receivables`, data);
    // Don't fetch receivables here - the page component will do it with proper month filters
    return response.data;
  },

  updateReceivable: async (id, data) => {
    const response = await axios.put(`${API_URL}/api/finance/receivables/${id}`, data);
    await get().fetchReceivables();
    return response.data;
  },

  receiveReceivable: async (id, data) => {
    const response = await axios.post(`${API_URL}/api/finance/receivables/${id}/receive`, data);
    await get().fetchReceivables();
    return response.data;
  },

  getReceivable: async (id) => {
    const response = await axios.get(`${API_URL}/api/finance/receivables/${id}`);
    return response.data;
  },

  updateCustomer: async (id, data) => {
    const response = await axios.put(`${API_URL}/api/finance/customers/${id}`, data);
    set((state) => ({
      customers: state.customers.map((c) => (c.id === id ? response.data : c))
    }));
    return response.data;
  },

  getCustomer: async (id) => {
    const response = await axios.get(`${API_URL}/api/finance/customers/${id}`);
    return response.data;
  },

  fetchCashFlow: async (params) => {
    set({ cashFlowLoading: true });
    try {
      const response = await axios.get(`${API_URL}/api/finance/cash-flow`, { params });
      set({ cashFlow: response.data, cashFlowLoading: false });
    } catch (error) {
      set({ cashFlowLoading: false });
      throw error;
    }
  },

  fetchDashboard: async (params = {}) => {
    set({ dashboardLoading: true, dashboardError: null });
    try {
      const response = await axios.get(`${API_URL}/api/finance/dashboard`, { params });
      set({ dashboard: response.data, dashboardLoading: false });
    } catch (error) {
      // Don't propagate - just set error state (401 is handled by interceptor)
      const errorMsg = error.response?.data?.detail || 'Erro ao carregar dashboard';
      set({ dashboardLoading: false, dashboardError: errorMsg });
      console.error('Dashboard fetch error:', error);
    }
  },

  fetchVendors: async () => {
    const response = await axios.get(`${API_URL}/api/finance/vendors`);
    set({ vendors: response.data });
  },

  createVendor: async (data) => {
    const response = await axios.post(`${API_URL}/api/finance/vendors`, data);
    set((state) => ({ vendors: [...state.vendors, response.data] }));
    return response.data;
  },

  fetchCustomers: async () => {
    const response = await axios.get(`${API_URL}/api/finance/customers`);
    set({ customers: response.data });
  },

  createCustomer: async (data) => {
    const response = await axios.post(`${API_URL}/api/finance/customers`, data);
    set((state) => ({ customers: [...state.customers, response.data] }));
    return response.data;
  },

  fetchCategories: async (type = null) => {
    const params = type ? { type } : {};
    const response = await axios.get(`${API_URL}/api/finance/categories`, { params });
    set({ categories: response.data });
  },

  createCategory: async (data) => {
    const response = await axios.post(`${API_URL}/api/finance/categories`, data);
    set((state) => ({ categories: [...state.categories, response.data] }));
    return response.data;
  },

  fetchCostCenters: async () => {
    const response = await axios.get(`${API_URL}/api/finance/cost-centers`);
    set({ costCenters: response.data });
  },

  createCostCenter: async (data) => {
    const response = await axios.post(`${API_URL}/api/finance/cost-centers`, data);
    set((state) => ({ costCenters: [...state.costCenters, response.data] }));
    return response.data;
  }
}));

// Users Store
export const useUsersStore = create((set) => ({
  users: [],
  isLoading: false,

  fetchUsers: async () => {
    set({ isLoading: true });
    try {
      const response = await axios.get(`${API_URL}/api/users/`);
      set({ users: response.data, isLoading: false });
    } catch (error) {
      set({ isLoading: false, users: [] });
      // Don't throw error for 403 - user just doesn't have permission
      if (error.response?.status !== 403) {
        throw error;
      }
    }
  },

  createUser: async (data) => {
    const response = await axios.post(`${API_URL}/api/users/`, data);
    set((state) => ({ users: [...state.users, response.data] }));
    return response.data;
  },

  updateUser: async (id, data) => {
    await axios.put(`${API_URL}/api/users/${id}`, data);
    set((state) => ({
      users: state.users.map((u) => (u.id === id ? { ...u, ...data } : u))
    }));
  },

  deactivateUser: async (id) => {
    await axios.delete(`${API_URL}/api/users/${id}`);
    set((state) => ({
      users: state.users.map((u) => (u.id === id ? { ...u, is_active: false } : u))
    }));
  },

  setUserPassword: async (id, password) => {
    await axios.put(`${API_URL}/api/users/${id}/password`, { password });
  },

  checkUserHasPassword: async (id) => {
    const response = await axios.get(`${API_URL}/api/users/${id}/has-password`);
    return response.data.has_password;
  }
}));

// Audit Store
export const useAuditStore = create((set) => ({
  logs: [],
  total: 0,
  page: 1,
  isLoading: false,

  fetchLogs: async (params = {}) => {
    set({ isLoading: true });
    try {
      const response = await axios.get(`${API_URL}/api/audit/logs`, { params });
      set({
        logs: response.data.logs,
        total: response.data.total,
        page: response.data.page,
        isLoading: false
      });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  }
}));

// Bank Account Filter Store - persisted to localStorage
// Used to filter financial views by selected bank accounts
export const useAccountFilterStore = create(
  persist(
    (set, get) => ({
      // Selected account IDs (empty = all accounts selected)
      selectedAccountIds: [],
      // Whether all accounts are selected (default: true)
      allSelected: true,
      
      // Toggle a single account
      toggleAccount: (accountId) => {
        const { selectedAccountIds, allSelected } = get();
        if (allSelected) {
          // If all were selected, now select only this one
          set({ selectedAccountIds: [accountId], allSelected: false });
        } else {
          const newSelected = selectedAccountIds.includes(accountId)
            ? selectedAccountIds.filter(id => id !== accountId)
            : [...selectedAccountIds, accountId];
          set({ selectedAccountIds: newSelected, allSelected: false });
        }
      },
      
      // Select all accounts
      selectAll: () => {
        set({ selectedAccountIds: [], allSelected: true });
      },
      
      // Clear selection (none selected)
      selectNone: () => {
        set({ selectedAccountIds: [], allSelected: false });
      },
      
      // Set specific accounts
      setSelectedAccounts: (accountIds) => {
        set({ selectedAccountIds: accountIds, allSelected: false });
      },
      
      // Check if an account is selected
      isAccountSelected: (accountId) => {
        const { selectedAccountIds, allSelected } = get();
        return allSelected || selectedAccountIds.includes(accountId);
      },
      
      // Get filter query param for API calls
      getFilterParam: () => {
        const { selectedAccountIds, allSelected } = get();
        // If all selected OR none selected, return null (no filter = show all)
        if (allSelected || selectedAccountIds.length === 0) return null;
        // Only filter when specific accounts are selected
        return selectedAccountIds.join(',');
      }
    }),
    {
      name: 'sintonia-account-filter',
      partialize: (state) => ({ 
        selectedAccountIds: state.selectedAccountIds,
        allSelected: state.allSelected
      })
    }
  )
);
